import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  History, Search, Calendar, BarChart3,
  Loader2, AlertCircle, Inbox, ShoppingCart, Package,
  TrendingUp, Info, X, AlertTriangle,
} from 'lucide-react';
import { useMyStoricoAcquisti, MyStoricoItem } from '../../hooks/useMyStoricoAcquisti';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';
import type { Product } from '../../types';
import { QtyInput } from '../../components/product/QtyInput';
import { usePageSize } from '../../hooks/usePageSize';

const PAGE_SIZES = [25, 50, 75, 100];

const PERIODI = [
  { v: 3,  l: '3 mesi' },
  { v: 6,  l: '6 mesi' },
  { v: 12, l: '12 mesi' },
  { v: 24, l: '24 mesi' },
];

/* ── Stock semaforo ── */
function StockBars({ stock }: { stock: number | null }) {
  if (stock === null || stock === undefined) {
    return (
      <span className="text-[10px] text-amber-600 font-semibold bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200" title="Disponibilità da verificare">
        Verifica
      </span>
    );
  }
  const level = stock <= 0 ? 0 : stock <= 2 ? 1 : stock <= 10 ? 2 : 3;
  const color = ['bg-rose-400', 'bg-orange-400', 'bg-amber-400', 'bg-emerald-500'][level];
  return (
    <div className="flex items-end gap-[3px]" title={`Stock: ${stock}`} data-testid="stock-bars">
      {[1, 2, 3].map((bar) => (
        <span key={bar} className={`w-[5px] rounded-sm ${bar <= level ? color : 'bg-muted/40'}`}
          style={{ height: `${bar * 4 + 4}px` }} />
      ))}
    </div>
  );
}

/* ── KPI card ── */
function KpiCard({ icon, label, value, highlight, loading }: {
  icon: React.ReactNode; label: string; value: React.ReactNode; highlight?: boolean; loading?: boolean;
}) {
  return (
    <div className={`rounded-xl border p-4 ${highlight ? 'bg-[hsl(210,40%,97%)] border-[hsl(210,64%,29%)]/30' : 'bg-white'}`}>
      <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground uppercase tracking-wider font-semibold mb-1">
        {icon} {label}
      </div>
      <div className={`text-2xl font-bold tabular-nums ${highlight ? 'text-[hsl(210,64%,29%)]' : ''}`}>
        {loading
          ? <span className="inline-block w-12 h-6 bg-[hsl(0,0%,93%)] rounded animate-pulse" />
          : value}
      </div>
    </div>
  );
}

/* ── Modale Storico Prodotto ── */
function ProductHistoryModal({ item, onClose }: { item: MyStoricoItem; onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      data-testid="storico-modal"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col border border-slate-200">

        {/* Intestazione modale */}
        <div className="px-5 py-3.5 flex items-center justify-between border-b">
          <h2 className="font-semibold text-base text-slate-800">Storico prodotto</h2>
          <button onClick={onClose}
            data-testid="storico-modal-close"
            className="w-7 h-7 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Riepilogo prodotto — riga compatta */}
        <div className="px-5 py-3 bg-slate-50 border-b flex items-center gap-4">
          <div className="min-w-[7rem]">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Referenza</div>
            <div className="font-mono font-bold text-sm text-slate-700 flex items-center gap-1.5">
              {item.sku}
              {item.isKit && (
                <span className="inline-flex items-center px-1 py-px rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300">
                  <Package className="w-2.5 h-2.5 mr-0.5" />Kit
                </span>
              )}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Nome prodotto</div>
            <div className="text-sm font-medium text-slate-700 truncate">{item.title}</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-0.5">Prezzo attuale</div>
            <div className="font-mono font-semibold text-sm text-slate-800">
              {item.prezzoCorrente != null
                ? `€ ${item.prezzoCorrente.toFixed(3).replace('.', ',')}`
                : '—'}
            </div>
          </div>
        </div>

        {/* Tabella ordini */}
        <div className="overflow-auto max-h-72">
          {item.ordini.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nessun ordine disponibile.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-slate-500 text-[11px] font-semibold uppercase tracking-wide border-b">
                  <th className="px-4 py-2.5 text-left">Data</th>
                  <th className="px-4 py-2.5 text-left">Ordine</th>
                  <th className="px-4 py-2.5 text-right">Acquistato</th>
                  {/* "Prezzo attuale" è solo quello del riepilogo: qui sono i prezzi pagati. */}
                  <th className="px-4 py-2.5 text-right">Prezzo</th>
                </tr>
              </thead>
              <tbody>
                {item.ordini.map((o, i) => (
                  <tr key={i}
                    className={`border-b last:border-b-0 ${i % 2 === 1 ? 'bg-slate-50/60' : 'bg-white'} hover:bg-slate-50 transition-colors`}>
                    <td className="px-4 py-2.5 tabular-nums text-muted-foreground">
                      {o.date
                        ? new Date(o.date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })
                        : '—'}
                    </td>
                    <td className="px-4 py-2.5">
                      <Link to={
                          o.tipork && o.anno && o.serie && o.numdoc
                            ? `/area-clienti/ordini/${o.tipork}/${o.anno}/${o.serie}/${o.numdoc}`
                            : `/area-clienti/ordini`
                        }
                        onClick={onClose}
                        className="font-mono text-xs font-semibold text-slate-700 hover:text-[hsl(210,64%,29%)] hover:underline">
                        {o.id || '—'}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-right font-semibold tabular-nums">
                      {o.qty} Ud.
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-muted-foreground">
                      {o.prezzo > 0 ? `€ ${o.prezzo.toFixed(3).replace('.', ',')}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3.5 border-t flex justify-center">
          <button onClick={onClose}
            data-testid="storico-modal-accept"
            className="px-8 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Chiudi
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────── */
export default function StoricoAcquistiPage() {
  const [periodoMesi, setPeriodoMesi] = useState(12);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<'codice' | 'spesa' | 'quantita' | 'recente'>('codice');
  const [pageSize, setPageSize] = usePageSize('storico', PAGE_SIZES);
  const [qtys, setQtys] = useState<Record<string, number>>({});
  const [modalItem, setModalItem] = useState<MyStoricoItem | null>(null);
  const { addItem, items: cartItems } = useCart();
  const { data, loading, error } = useMyStoricoAcquisti(periodoMesi);

  const inCartSkus = useMemo(() => new Set(cartItems.map(i => i.sku)), [cartItems]);
  const cartQty = (sku: string) => cartItems.find(i => i.sku === sku)?.qty ?? 0;

  const filtered = useMemo(() => {
    const items = data?.items ?? [];
    const q = search.trim().toLowerCase();
    const out = q
      ? items.filter(a => a.sku.toLowerCase().includes(q) || a.title.toLowerCase().includes(q))
      : items;
    return [...out].sort((a, b) => {
      if (sortBy === 'codice')   return a.sku.localeCompare(b.sku, 'it', { numeric: true }) || (a.color || '').localeCompare(b.color || '');
      if (sortBy === 'spesa')    return b.totSpesa - a.totSpesa;
      if (sortBy === 'quantita') return b.totQuantita - a.totQuantita;
      return (b.ultimoAcquisto || '').localeCompare(a.ultimoAcquisto || '');
    });
  }, [data, search, sortBy]);

  const displayed = filtered.slice(0, pageSize);
  const totals = data?.totals;

  /* Qty default = 1 (rimossa la logica "minimo vendibile" — non più prevista) */
  const getQty = (sku: string) => qtys[sku] ?? 1;
  const setQty = (sku: string, v: number) => setQtys(prev => ({ ...prev, [sku]: v }));

  const handleAddToCart = (item: MyStoricoItem) => {
    const stock = item.stockCorrente;
    if (stock === null) return; // disponibilità da verificare
    if (stock <= 0) return;     // esaurito
    const alreadyInCart = cartQty(item.sku);
    const remaining = Math.max(0, stock - alreadyInCart);
    if (remaining <= 0) return; // già nel carrello al massimo
    const qty = Math.min(getQty(item.sku), remaining);
    const product = {
      sku: item.sku, title: item.title, brand: item.brand ?? '',
      category: '', compatibility: '', type: item.isKit ? 'Kit' : 'Compatibile',
      yield: '', color: '',
      listino10: item.prezzoCorrente ?? undefined,
      stock: stock,
    } as unknown as Product;
    const added = addItem(product, qty);
    if (added > 0) toast.success(`${item.sku} — ${added} pz aggiunti al carrello`);
  };

  return (
    <div data-testid="storico-acquisti-page" className="space-y-5">

      {/* Modale storico prodotto */}
      {modalItem && (
        <ProductHistoryModal item={modalItem} onClose={() => setModalItem(null)} />
      )}

      {/* ── Titolo ── */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
          <History className="w-6 h-6 text-[hsl(210,64%,29%)]" />
          Storico Acquisti
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Articoli acquistati negli ultimi {periodoMesi} mesi · dati live dal gestionale NTS.
        </p>
      </div>

      {/* ── KPI ── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="storico-kpis">
        <KpiCard icon={<History className="w-4 h-4" />} label="SKU acquistati"
          value={totals?.skuCount ?? 0} loading={loading} />
        <KpiCard icon={<BarChart3 className="w-4 h-4" />} label="Pezzi totali"
          value={totals?.totQuantita ?? 0} loading={loading} />
        <KpiCard icon={<Calendar className="w-4 h-4" />} label="Ordini"
          value={totals?.ordiniCount ?? 0} loading={loading} />
        <KpiCard icon={<TrendingUp className="w-4 h-4" />} label="Spesa netta"
          value={totals ? `€ ${totals.totSpesa.toFixed(2).replace('.', ',')}` : '€ 0,00'}
          loading={loading} highlight />
      </div>

      {/* ── Toolbar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border bg-white p-1">
          {PERIODI.map(p => (
            <button key={p.v} onClick={() => setPeriodoMesi(p.v)}
              data-testid={`periodo-${p.v}`}
              className={`h-8 px-3 rounded text-xs font-medium transition-colors ${
                periodoMesi === p.v
                  ? 'bg-[hsl(210,64%,29%)] text-white'
                  : 'text-[#3a3a36] hover:bg-[hsl(210,40%,97%)]'
              }`}>{p.l}</button>
          ))}
        </div>

        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Nome prodotto o referenza..." data-testid="storico-search"
            className="w-full h-9 pl-9 pr-8 rounded-lg border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(210,64%,29%)]/30" />
          {search && (
            <button onClick={() => setSearch('')}
              data-testid="storico-search-clear"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>

        <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}
          data-testid="storico-sort"
          className="h-9 px-3 rounded-lg border bg-white text-sm focus:outline-none">
          <option value="codice">Ordina: codice e colore</option>
          <option value="spesa">Ordina: spesa</option>
          <option value="quantita">Ordina: quantità</option>
          <option value="recente">Ordina: ultimo acquisto</option>
        </select>

        <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
          data-testid="storico-pagesize"
          className="h-9 px-3 rounded-lg border bg-white text-sm focus:outline-none">
          {PAGE_SIZES.map(n => <option key={n} value={n}>{n} articoli</option>)}
        </select>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {/* ── Tabella ── */}
      <div className="rounded-xl border bg-white overflow-hidden shadow-sm">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Caricamento storico…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="storico-empty">
            <Inbox className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
            <p className="text-sm font-semibold">
              {search ? 'Nessun risultato per questa ricerca.' : 'Nessun acquisto nel periodo selezionato.'}
            </p>
          </div>
        ) : (
          <>
            {/* Header colonne */}
            <div className="hidden md:grid grid-cols-[2.5rem_9rem_1fr_3.5rem_7.5rem_7rem_6.5rem] bg-[hsl(210,64%,29%)] text-white text-[11px] font-semibold uppercase tracking-wide">
              <div className="px-2 py-2.5" />
              <div className="px-3 py-2.5">Referenza</div>
              <div className="px-3 py-2.5">Prodotto</div>
              <div className="px-3 py-2.5 text-center">Disp.</div>
              <div className="px-3 py-2.5 text-center">Quantità</div>
              <div className="px-3 py-2.5 text-right">Prezzo/Ud.</div>
              <div className="px-2 py-2.5 text-center">Acquista di nuovo</div>
            </div>

            {/* Righe */}
            <div data-testid="storico-table">
              {displayed.map((row, idx) => {
                const isInCart = inCartSkus.has(row.sku);
                const stockMax = row.stockCorrente;
                const alreadyInCart = cartQty(row.sku);
                const remainingStock = stockMax !== null ? Math.max(0, stockMax - alreadyInCart) : null;
                const daVerificare = stockMax === null;
                const esaurito = stockMax !== null && stockMax <= 0;
                const maxedInCart = stockMax !== null && alreadyInCart >= stockMax;
                const acquistabile = !daVerificare && !esaurito && !maxedInCart;
                const qty = getQty(row.sku);

                return (
                  <div key={row.sku} data-testid={`storico-row-${row.sku}`}
                    className={`border-b last:border-b-0 ${idx % 2 === 1 ? 'bg-[hsl(210,40%,99%)]' : 'bg-white'}`}>
                    <div className="grid md:grid-cols-[2.5rem_9rem_1fr_3.5rem_7.5rem_7rem_6.5rem] items-center gap-0 py-2.5 px-3 md:px-0">

                      {/* Pulsante "i" → apre modale */}
                      <div className="hidden md:flex items-center justify-center px-1">
                        <button onClick={() => setModalItem(row)}
                          title="Vedi storico ordini per questo prodotto"
                          data-testid={`storico-history-btn-${row.sku}`}
                          className="w-5 h-5 rounded-full border border-[hsl(210,64%,29%)]/50 text-[hsl(210,64%,29%)] flex items-center justify-center hover:bg-[hsl(210,64%,29%)] hover:text-white hover:border-[hsl(210,64%,29%)] transition-colors">
                          <Info className="w-3 h-3" />
                        </button>
                      </div>

                      {/* Referenza */}
                      <div className="px-3 md:px-2 flex flex-col gap-0.5 min-w-0">
                        <Link to={`/prodotti/_/${row.sku}`}
                          data-testid={`storico-sku-${row.sku}`}
                          className="font-mono text-xs font-bold text-[hsl(210,64%,29%)] hover:underline leading-none truncate">
                          {row.sku}
                        </Link>
                        {(row.brand || row.color) && (
                          <span className="text-[10px] text-muted-foreground truncate" data-testid={`storico-marca-colore-${row.sku}`}>
                            {[row.brand, row.color].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {row.isKit && (
                          <span className="inline-flex items-center w-fit px-1 py-px rounded text-[9px] font-bold uppercase bg-amber-100 text-amber-700 border border-amber-300 mt-0.5">
                            <Package className="w-2.5 h-2.5 mr-0.5" />Kit
                          </span>
                        )}
                      </div>

                      {/* Descrizione prodotto */}
                      <div className="px-3 md:px-2 min-w-0">
                        <Link to={`/prodotti/_/${row.sku}`}
                          className="text-sm font-medium hover:text-[hsl(210,64%,29%)] hover:underline line-clamp-2 leading-snug">
                          {row.title}
                        </Link>
                        {/* Mobile: riepilogo compatto */}
                        <div className="md:hidden text-xs text-muted-foreground mt-0.5">
                          {row.numOrdini} {row.numOrdini === 1 ? 'ordine' : 'ordini'} · {row.totQuantita} pz
                          {row.prezzoCorrente != null && ` · € ${row.prezzoCorrente.toFixed(2).replace('.', ',')}`}
                        </div>
                      </div>

                      {/* Disponibilità */}
                      <div className="hidden md:flex items-center justify-center px-2">
                        <StockBars stock={row.stockCorrente} />
                      </div>

                      {/* Qty stepper */}
                      <div className="flex items-center justify-center px-2 mt-2 md:mt-0">
                        {acquistabile ? (
                          <QtyInput
                            size="sm"
                            value={qty}
                            onChange={(v) => setQty(row.sku, v)}
                            max={remainingStock ?? 999}
                          />
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>

                      {/* Prezzo/Ud. */}
                      <div className="hidden md:block px-2 text-right">
                        {row.prezzoCorrente != null ? (
                          <>
                            <span className="font-mono font-semibold text-sm">
                              € {row.prezzoCorrente.toFixed(3).replace('.', ',')}
                            </span>
                            <div className="text-[10px] text-muted-foreground">IVA escl.</div>
                          </>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </div>

                      {/* Pulsante carrello */}
                      <div className="flex items-center justify-center px-2 mt-2 md:mt-0">
                        {daVerificare ? (
                          <span
                            data-testid={`storico-da-verificare-${row.sku}`}
                            title="Disponibilità da verificare — contatta DTK"
                            className="h-8 w-8 rounded-lg flex items-center justify-center bg-amber-100 border border-amber-300 text-amber-600"
                          >
                            <AlertTriangle className="w-4 h-4" />
                          </span>
                        ) : esaurito ? (
                          <span
                            data-testid={`storico-esaurito-${row.sku}`}
                            title="Articolo esaurito"
                            className="h-8 px-2 rounded-lg flex items-center justify-center bg-red-50 border border-red-200 text-red-500 text-[10px] font-semibold"
                          >
                            Esaurito
                          </span>
                        ) : maxedInCart ? (
                          <span
                            data-testid={`storico-maxed-cart-${row.sku}`}
                            title={`Hai già ${alreadyInCart} pz nel carrello (scorte esaurite)`}
                            className="h-8 w-8 rounded-lg flex items-center justify-center bg-orange-500 text-white"
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </span>
                        ) : (
                          <button onClick={() => handleAddToCart(row)}
                            data-testid={`storico-add-cart-${row.sku}`}
                            title={isInCart ? `Nel carrello: ${alreadyInCart} pz — aggiungi ancora` : 'Aggiungi al carrello'}
                            className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors ${
                              isInCart
                                ? 'bg-orange-500 text-white hover:bg-orange-600'
                                : 'bg-[hsl(210,64%,29%)] text-white hover:bg-[hsl(210,64%,22%)]'
                            }`}>
                            <ShoppingCart className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Footer paginazione */}
            {filtered.length > pageSize && (
              <div className="px-4 py-3 border-t bg-[hsl(210,40%,98%)] flex items-center justify-between"
                data-testid="storico-pagination">
                <p className="text-xs text-muted-foreground">
                  Visualizzati <span className="font-semibold">{displayed.length}</span> di{' '}
                  <span className="font-semibold">{filtered.length}</span> articoli
                </p>
                <div className="flex gap-2">
                  {[50, 75, 100].filter(n => n > pageSize).slice(0, 2).map(n => (
                    <button key={n} onClick={() => setPageSize(n)}
                      className="h-7 px-3 rounded border text-xs font-medium hover:bg-white transition-colors">
                      Mostra {n}
                    </button>
                  ))}
                  <button onClick={() => setPageSize(filtered.length)}
                    className="h-7 px-3 rounded border text-xs font-medium hover:bg-white transition-colors">
                    Tutti
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
